import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Phone, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const locations = [
  { city: "Charlotte", phone: "(704) 599-3355", address: "2740 East WT Harris Blvd, Suite 200", note: null },
  { city: "Winston-Salem", phone: "(336) 761-0366", address: "326 S. Main Street", note: null },
  { city: "Charlotte (SouthPark)", phone: "(704) 599-3355", address: "6000 Fairview Road, Suite 1200", note: "By Appointment Only" },
  { city: "Mooresville", phone: "(704) 663-6213", address: "121 Rolling Hill Road", note: "By Appointment Only" }
];

const buildOnboardURL = () => {
  const params = new URLSearchParams();
  params.set('ref', 'welcome');

  const leadId = window.__kgob_lead_id;
  if (leadId) params.set('wl', leadId);

  const urlParams = new URLSearchParams(window.location.search);
  const utm_source = urlParams.get('utm_source');
  const utm_medium = urlParams.get('utm_medium');
  const utm_campaign = urlParams.get('utm_campaign');
  if (utm_source) params.set('utm_source', utm_source);
  if (utm_medium) params.set('utm_medium', utm_medium);
  if (utm_campaign) params.set('utm_campaign', utm_campaign);

  return `https://app.kgob.com/onboard?${params.toString()}`;
};

export const ClosingSection = () => {
  const handleCTAClick = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const assessment = window.__kgob_assessment;

    // Fire-and-forget CTA tracking with timeout
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 5000);

    fetch(`${API_URL}/api/v1/welcome/track-cta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lead_number: window.__kgob_lead_id || null,
        email: window.__kgob_lead_email || null,
        destination: '/onboard',
        utm_source: urlParams.get('utm_source') || null,
        utm_medium: urlParams.get('utm_medium') || null,
        utm_campaign: urlParams.get('utm_campaign') || null,
        page_url: window.location.href,
        assessment_completed: !!(assessment && assessment.completed)
      }),
      signal: controller.signal
    }).catch(() => {});
  }, []);

  return (
    <section
      className="relative bg-[#0B2240] py-20 lg:py-28 overflow-hidden"
      data-testid="welcome-closing"
    >
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-white">
            Your finances deserve senior-level attention.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            We intentionally keep our client roster focused so that every business and individual gets direct access
            to experienced CPAs — not junior staff, not outsourced teams. If you've been thinking about making a change,
            the best time to start the conversation is before tax season — not during it.
          </p>

          <div className="mt-8">
            <p className="text-[11px] tracking-[0.16em] uppercase text-[#D4AF37]/85 font-semibold mb-3" data-testid="welcome-approval-note">
              All new client engagements are subject to KGOB review and acceptance
            </p>
            <a
              href={buildOnboardURL()}
              onClick={handleCTAClick}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl text-[#0B2240] font-semibold text-base bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_4px_20px_rgba(212,175,55,0.3)] hover:brightness-110 transition-all duration-200"
              data-testid="closing-cta"
            >
              Get Started with KGOB
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </motion.div>

        {/* Direct Contact */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <p className="text-center text-sm text-white/40 uppercase tracking-widest mb-6">
            Prefer to reach out directly?
          </p>

          <div className="flex items-center justify-center mb-8">
            <a
              href="mailto:connect@kgob.com"
              className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E3C96E] text-sm font-medium transition-colors"
              data-testid="closing-email"
            >
              <Mail className="h-4 w-4" />
              connect@kgob.com
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {locations.map((loc) => (
              <div
                key={loc.city}
                className="rounded-xl p-4 border border-white/10 bg-white/5"
                data-testid={`location-${loc.city.toLowerCase().replace(/[^a-z]/g, '')}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-[#D4AF37]" />
                  <p className="text-white text-sm font-medium">{loc.city}</p>
                </div>
                <p className="text-white/50 text-xs leading-relaxed mb-2">{loc.address}</p>
                {loc.note && (
                  <p className="text-[#D4AF37]/60 text-xs italic mb-2">{loc.note}</p>
                )}
                <a data-testid="closing-section-a-1"
                  href={`tel:${loc.phone.replace(/\D/g, '')}`}
                  className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs transition-colors"
                >
                  <Phone className="h-3 w-3" />
                  {loc.phone}
                </a>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
